import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';


const ComplianceCharts = () => {
  const [selectedChart, setSelectedChart] = useState('trends');

  const chartOptions = [
    { value: 'trends', label: 'Violation Trends' },
    { value: 'platforms', label: 'Platform Analysis' },
    { value: 'teams', label: 'Team Performance' },
    { value: 'severity', label: 'Severity Distribution' }
  ];

  const trendsData = [
    { month: 'Jan', violations: 45, resolved: 42 },
    { month: 'Feb', violations: 52, resolved: 48 },
    { month: 'Mar', violations: 38, resolved: 35 },
    { month: 'Apr', violations: 61, resolved: 58 },
    { month: 'May', violations: 43, resolved: 41 },
    { month: 'Jun', violations: 29, resolved: 27 },
    { month: 'Jul', violations: 35, resolved: 33 },
    { month: 'Aug', violations: 48, resolved: 45 },
    { month: 'Sep', violations: 31, resolved: 29 }
  ];

  const platformData = [
    { platform: 'Facebook', violations: 125, percentage: 32 },
    { platform: 'LinkedIn', violations: 98, percentage: 25 },
    { platform: 'Twitter', violations: 87, percentage: 22 },
    { platform: 'Instagram', violations: 54, percentage: 14 },
    { platform: 'Website', violations: 28, percentage: 7 }
  ];

  const teamData = [
    { team: 'Marketing', violations: 156, resolved: 142, efficiency: 91 },
    { team: 'Social Media', violations: 134, resolved: 118, efficiency: 88 },
    { team: 'Content', violations: 89, resolved: 82, efficiency: 92 },
    { team: 'Compliance', violations: 23, resolved: 23, efficiency: 100 }
  ];

  const severityData = [
    { name: 'Critical', value: 45, color: '#DC2626' },
    { name: 'High', value: 128, color: '#D97706' },
    { name: 'Medium', value: 187, color: '#0EA5E9' },
    { name: 'Low', value: 92, color: '#059669' }
  ];

  const renderChart = () => {
    switch (selectedChart) {
      case 'trends':
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-foreground">Violation Trends Over Time</h4>
              <div className="flex items-center space-x-4 text-xs">
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-3 bg-accent rounded-sm"></div>
                  <span className="text-muted-foreground">Violations</span>
                </div>
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-3 bg-success rounded-sm"></div>
                  <span className="text-muted-foreground">Resolved</span>
                </div>
              </div>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trendsData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                  <XAxis dataKey="month" stroke="var(--color-muted-foreground)" fontSize={12} />
                  <YAxis stroke="var(--color-muted-foreground)" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'var(--color-card)',
                      border: '1px solid var(--color-border)',
                      borderRadius: '8px'
                    }}
                  />
                  <Line type="monotone" dataKey="violations" stroke="var(--color-accent)" strokeWidth={2} />
                  <Line type="monotone" dataKey="resolved" stroke="var(--color-success)" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        );

      case 'platforms':
        return (
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-foreground">Violations by Platform</h4>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={platformData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                  <XAxis dataKey="platform" stroke="var(--color-muted-foreground)" fontSize={12} />
                  <YAxis stroke="var(--color-muted-foreground)" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'var(--color-card)',
                      border: '1px solid var(--color-border)',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar dataKey="violations" fill="var(--color-accent)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        );

      case 'teams':
        return (
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-foreground">Team Performance Analysis</h4>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={teamData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                  <XAxis dataKey="team" stroke="var(--color-muted-foreground)" fontSize={12} />
                  <YAxis stroke="var(--color-muted-foreground)" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'var(--color-card)',
                      border: '1px solid var(--color-border)',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar dataKey="violations" fill="var(--color-warning)" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="resolved" fill="var(--color-success)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        );

      case 'severity':
        return (
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-foreground">Violation Severity Distribution</h4>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={severityData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {severityData?.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry?.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'var(--color-card)',
                      border: '1px solid var(--color-border)',
                      borderRadius: '8px'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {severityData?.map((item) => (
                <div key={item?.name} className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item?.color }}></div>
                  <span className="text-sm text-muted-foreground">{item?.name}</span>
                  <span className="text-sm font-medium text-foreground">{item?.value}</span>
                </div>
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Compliance Analytics</h3>
          <p className="text-sm text-muted-foreground">Visual insights into compliance trends and patterns</p>
        </div>
        <div className="flex items-center space-x-3">
          <Select
            options={chartOptions}
            value={selectedChart}
            onChange={setSelectedChart}
            className="w-48"
          />
          <Button
            variant="outline"
            size="sm"
            iconName="Download"
            iconPosition="left"
          >
            Export
          </Button>
        </div>
      </div>

      {renderChart()}

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-border">
        <div className="text-center">
          <div className="text-2xl font-bold text-foreground">392</div>
          <div className="text-sm text-muted-foreground">Total Violations</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-success">365</div>
          <div className="text-sm text-muted-foreground">Resolved</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-warning">27</div>
          <div className="text-sm text-muted-foreground">Pending</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-accent">93%</div>
          <div className="text-sm text-muted-foreground">Resolution Rate</div>
        </div>
      </div>
    </div>
  );
};

export default ComplianceCharts;